import { Activity, Bot, CirclePlay, Play, Settings, SquareTerminal, Terminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { OS_LABEL, machineLiveness } from '@orgus/protocol';
import type { Job, Machine, Session } from '@orgus/protocol';
import {
  Button,
  Drawer,
  EmptyState,
  LiveNumber,
  Pressable,
  RelativeTime,
  RowStatus,
  StateBar,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TrendArea,
  formatBytes,
  formatDuration,
  formatInt,
  formatPercent,
  jobLabel,
  plural,
  sessionRowStatus,
  shortPath,
} from '@orgus/ui';
import type { DataSlot, RowStatusProps } from '@orgus/ui';
import type { MachineSample } from '../../lib/store.ts';
import { openRunCommand } from '../jobs/store.ts';
import { RuntimeList, installedRuntimes } from '../adapters/Runtimes.tsx';
import { openNewSession } from '../new-session/store.ts';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import { CPU_SLOT, MEM_SLOT, MachineStatus, OsTile, sessionCountsText, sessionStates } from './MachineCard.tsx';
import { useMachineDrain, workingCount } from './controls.ts';
import { MachineMenu } from './MachineMenu.tsx';
import type { MenuFailure } from './MachineMenu.tsx';
import { useCloud } from '../cloud/data.ts';
import { vmOfMachine } from '../cloud/model.ts';
import { setMachineWork } from '../scheduler/NodeControls.tsx';
import { ReadinessList } from './Readiness.tsx';

/** Sessions listed before the rest fold behind "Show all". */
const SESSIONS_SHOWN = 6;

/** One measure: its value rolling, the trend under it with guides at half and full. */
function Chart({ label, value, values, slot, note }: { label: string; value: number; values: number[]; slot: DataSlot; note?: string }) {
  return (
    <section aria-label={label} className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="text-caption text-fg-subtle">{label}</span>
        {note && <span className="ml-auto truncate text-caption text-fg-subtle num">{note}</span>}
      </div>
      <LiveNumber value={value} format={(n) => formatPercent(n)} className="font-display text-metric-sm text-fg num-display" />
      <TrendArea values={values} min={0} max={1} slot={slot} height={64} guides={[0.5, 1]} label={`${label}, recent samples, now ${formatPercent(value)}`} />
    </section>
  );
}

/** A command's one status, plain as in every row: running is cyan, failed red, the rest grey. */
function jobRowStatus(j: Job): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  if (j.state === 'running') return { tone: 'working', icon: 'working', label: jobLabel(j) };
  if (j.state === 'failed') return { tone: 'danger', icon: 'failed', label: j.exitCode != null ? `${jobLabel(j)}, exit ${j.exitCode}` : jobLabel(j) };
  if (j.state === 'passed') return { tone: 'neutral', icon: 'done', label: jobLabel(j) };
  return { tone: 'neutral', icon: 'stopped', label: jobLabel(j) };
}

type Tab = 'activity' | 'agents' | 'settings';

/**
 * One machine in detail: its facts in the header, CPU and memory, then three tabs: what runs on
 * it (load, sessions, commands), what its agent found on it (harnesses signed in, git,
 * repositories) and the agent CLIs it has, and its settings (autonomy, updates, the Orgus agent). Start session is the one primary action.
 */
export function MachineDrawer({
  machine: m,
  history,
  sessions,
  jobs,
  now,
  onClose,
  onRevoke,
  updateTo = null,
  onUpdate,
  onFail,
  children,
  load,
  onOpenJob,
}: {
  machine: Machine | undefined;
  history?: MachineSample[];
  sessions: Session[];
  jobs: Job[];
  now: number;
  onClose: () => void;
  onRevoke: () => void;
  /** The release Update agent offers (fleet), or null. */
  updateTo?: string | null;
  onUpdate?: () => Promise<void>;
  /** A menu action that failed, for the page's banner. */
  onFail: (f: MenuFailure) => void;
  /** Feature sections for the Settings tab (autonomy, agent and updates, about the agent). */
  children?: ReactNode;
  /** What uses the machine, first on the Activity tab (module machine-load). */
  load?: ReactNode;
  /** Open a command's output (and Cancel, while it runs). */
  onOpenJob?: (jobId: string) => void;
}) {
  const live = m ? machineLiveness(m, now) : 'offline';
  const drain = useMachineDrain(m?.id) ?? null;
  // A VM Orgus created (ADR 0106 decision 3): its status and menu items, as on its card.
  const cloud = useCloud().state;
  const vm = m ? vmOfMachine(cloud, m.id) : null;
  const dim = live !== 'online';
  const memUsed = m && m.memTotal > 0 ? 1 - m.memFree / m.memTotal : 0;
  const liveSessions = sessions.filter((s) => s.status !== 'ended');
  const loads = useMemo(() => (history ?? []).map((s) => s.load), [history]);
  const mems = useMemo(() => (history ?? []).map((s) => s.mem), [history]);
  const running = jobs.filter((j) => j.state === 'running').length;
  const [tab, setTab] = useState<Tab>('activity');
  const [allSessions, setAllSessions] = useState(false);
  // Another machine opens on its activity, its list folded again.
  useEffect(() => {
    setTab('activity');
    setAllSessions(false);
  }, [m?.id]);
  const installed = m ? installedRuntimes(m) : 0;
  const shownSessions = allSessions ? sessions : sessions.slice(0, SESSIONS_SHOWN);
  return (
    <Drawer
      open={!!m}
      onOpenChange={(v) => !v && onClose()}
      width={640}
      layout="rows"
      actions={m && <MachineMenu machine={m} running={liveSessions.length} updateTo={updateTo} onUpdate={onUpdate} onRemove={onRevoke} onFail={onFail} vm={vm} />}
      eyebrow={
        m && (
          <>
            <span className="text-caption text-fg-subtle">Machine</span>
            <MachineStatus machine={m} now={now} coloured working={workingCount(sessions.filter((s) => s.status !== 'ended'), jobs)} drain={drain} vm={vm} />
          </>
        )
      }
      title={
        m ? (
          <span className="flex min-w-0 items-center gap-3">
            <OsTile os={m.os} quiet={dim} />
            <span className="truncate">{m.name}</span>
          </span>
        ) : (
          'Machine'
        )
      }
      description={
        m && (
          <FactGrid
            label="Machine facts"
            lines={[
              [
                { label: 'System', value: [OS_LABEL[m.os], m.arch].filter(Boolean).join(', ') },
                { label: 'Hostname', value: m.hostname || 'Not reported', mono: !!m.hostname, title: m.hostname || undefined },
                { label: 'Orgus agent', value: m.agentVersion || 'Unknown' },
                dim
                  ? { label: 'Last report', value: <RelativeTime ts={m.lastSeen} /> }
                  : { label: 'Up for', value: m.connectedAt ? formatDuration(now - m.connectedAt) : 'Not reported' },
              ],
              [{ label: 'Labels', value: m.labels.length ? m.labels.join(', ') : null }],
            ]}
          />
        )
      }
      footer={
        m && (
          <>
            {live === 'online' && drain && (
              <Button variant="primary" icon={<CirclePlay />} className="ml-auto" onClick={() => void setMachineWork(m, true, liveSessions.length).catch((error: unknown) => onFail({ what: `${m.name} was not enabled`, error }))}>
                Enable new work
              </Button>
            )}
            {live === 'online' && !drain && (
              <Button variant="primary" icon={<SquareTerminal />} className="ml-auto" onClick={() => openNewSession({ machineId: m.id })}>
                Start session
              </Button>
            )}
          </>
        )
      }
    >
      {m && (
        <div className="flex flex-col gap-6 px-6 py-4">
          {/* Offline or not reporting: no readings, so no charts (the header says since when). */}
          {!dim && (
            <div className="grid grid-cols-2 gap-6">
              <Chart label="CPU" value={m.load} values={loads} slot={CPU_SLOT} note={`${formatInt(m.cpus)} CPUs`} />
              <Chart label="Memory" value={memUsed} values={mems} slot={MEM_SLOT} note={`${formatBytes(m.memTotal - m.memFree)} of ${formatBytes(m.memTotal, 0)}`} />
            </div>
          )}

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex flex-col gap-6">
            <TabsList aria-label="Machine views">
              <TabsTrigger value="activity" icon={<Activity />} count={liveSessions.length || undefined}>
                Activity
              </TabsTrigger>
              <TabsTrigger value="agents" icon={<Bot />} count={installed || undefined}>
                Agents
              </TabsTrigger>
              <TabsTrigger value="settings" icon={<Settings />}>
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="activity" className="flex flex-col gap-6">
              {!dim && load}

              <DetailSection title="Sessions" aside={liveSessions.length ? sessionCountsText(liveSessions) : plural(sessions.length, 'session') + ' in the last 7 days'}>
                {liveSessions.length > 0 && <StateBar counts={sessionStates(liveSessions)} className="mb-2" />}
                {sessions.length ? (
                  <ul className="-mx-2 flex flex-col">
                    {shownSessions.map((s) => (
                      <li key={s.id} className="motion-arrive">
                        <Link
                          to={`/sessions?session=${encodeURIComponent(s.id)}`}
                          className="flex h-row items-center gap-3 rounded-control px-2 transition-colors duration-(--duration-fast) hover:bg-hover"
                        >
                          <RowStatus {...sessionRowStatus(s.status)} hideLabel focusable={false} />
                          <span className="min-w-0 flex-1 truncate text-ui text-fg">{s.title}</span>
                          <span className="shrink-0 text-caption text-fg-subtle num">
                            <RelativeTime ts={s.lastActivity} />
                          </span>
                        </Link>
                      </li>
                    ))}
                    {sessions.length > SESSIONS_SHOWN && (
                      <li>
                        <Pressable
                          aria-expanded={allSessions}
                          onClick={() => setAllSessions((v) => !v)}
                          className="flex h-row items-center rounded-control px-2 text-caption text-fg-subtle hover:bg-hover hover:text-fg-muted"
                        >
                          {allSessions ? 'Show fewer' : `Show all ${formatInt(sessions.length)}`}
                        </Pressable>
                      </li>
                    )}
                  </ul>
                ) : (
                  <EmptyState
                    icon={<SquareTerminal />}
                    title="No sessions on this machine"
                    description="Sessions in its watched projects show here, live"
                  />
                )}
              </DetailSection>

              <DetailSection
                title="Commands"
                aside={running ? `${formatInt(running)} running` : jobs.length ? formatInt(jobs.length) : undefined}
                actions={
                  live === 'online' ? (
                    <Button size="sm" variant="ghost" icon={<Play />} onClick={() => openRunCommand({ machineId: m.id })}>
                      Run command
                    </Button>
                  ) : undefined
                }
              >
                {jobs.length ? (
                  <ul className="-mx-2 flex flex-col">
                    {jobs.map((j) => (
                      <li key={j.id} className="motion-arrive">
                        <Pressable
                          onClick={() => onOpenJob?.(j.id)}
                          className="flex h-row-2line w-full items-center gap-3 rounded-control px-2 text-left transition-colors duration-(--duration-fast) hover:bg-hover"
                        >
                          <Terminal className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-mono text-caption text-fg">{j.command}</span>
                            <span className="truncate text-caption text-fg-subtle">{shortPath(j.cwd, 48)}</span>
                          </span>
                          <span className="w-28 shrink-0 text-right">
                            <RowStatus {...jobRowStatus(j)} focusable={false} />
                          </span>
                          <span className="w-12 shrink-0 text-right text-caption text-fg-subtle num">{formatDuration((j.endedAt ?? now) - j.startedAt)}</span>
                        </Pressable>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-ui text-fg-subtle">No commands run from Orgus yet</p>
                )}
              </DetailSection>
            </TabsContent>

            <TabsContent value="agents" className="flex flex-col gap-6">
              <ReadinessList machine={m} />
              <RuntimeList machine={m} />
            </TabsContent>

            <TabsContent value="settings" className="flex flex-col gap-8">
              {children}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Drawer>
  );
}
